import { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { API_BASE } from '@/lib/api-base';
import type { PlatformCall } from './common';

interface Policy {
  enabled: boolean;
  mode: 'APPROVAL' | 'AUTO';
  allow_password: boolean;
  allow_google: boolean;
  email_verification: boolean;
  google_client_id: string;
  public_base_url: string;
  allowed_email_domains: string[];
  blocked_email_domains: string[];
  block_free_email_providers: boolean;
  max_signups_per_ip_per_day: number;
  max_workspaces_per_email: number;
  verify_link_ttl_hours: number;
  terms_url: string;
  privacy_url: string;
  default_country: string;
  notify_emails: string[];
  approval_emails: string[];
  captcha_provider: 'none' | 'turnstile' | 'hcaptcha';
  captcha_site_key: string;
  captcha_secret_set: boolean;
  captcha_size: 'normal' | 'compact';
  captcha_max_score: number | null;
}

const CAPTCHA_LABEL: Record<Policy['captcha_provider'], string> = {
  none: 'Off',
  turnstile: 'Cloudflare Turnstile',
  hcaptcha: 'hCaptcha',
};
const CAPTCHA_KEYS_FROM: Record<Exclude<Policy['captcha_provider'], 'none'>, string> = {
  turnstile: 'Cloudflare dashboard → Turnstile → your widget',
  hcaptcha: 'dashboard.hcaptcha.com → Sites (site key) and Settings (secret)',
};

interface Env {
  multitenancy_enabled: boolean;
  smtp_enabled: boolean;
  debug: boolean;
  app_base_url: string;
  password_signup_blocker: string | null;
}

// Lists travel as comma-separated text in the form and as arrays on the wire.
type ListKey = 'allowed_email_domains' | 'blocked_email_domains' | 'notify_emails' | 'approval_emails';
const LIST_KEYS: string[] = ['allowed_email_domains', 'blocked_email_domains', 'notify_emails', 'approval_emails'];

// The risk score is typed as text ("0.", "0.7") and sent as text; blank = off.
type Draft = Omit<Policy, ListKey | 'captcha_max_score'> & Record<ListKey, string> & {
  captcha_secret: string;
  captcha_max_score: string;
};

const toDraft = (p: Policy): Draft => ({
  ...p,
  allowed_email_domains: p.allowed_email_domains.join(', '),
  blocked_email_domains: p.blocked_email_domains.join(', '),
  notify_emails: p.notify_emails.join(', '),
  approval_emails: (p.approval_emails ?? []).join(', '),
  captcha_secret: '',
  captcha_size: p.captcha_size ?? 'normal',
  captcha_max_score: p.captcha_max_score == null ? '' : String(p.captcha_max_score),
});

const list = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

interface Operator { email: string; full_name: string | null; is_active: boolean }

/**
 * Self-service signup policy, stored in the Master DB and applied within
 * seconds — no `.env` edit, no restart. Every save is audited.
 */
export function SignupSettingsTab({ call }: { call: PlatformCall }) {
  const [saved, setSaved] = useState<Policy | null>(null);
  const [env, setEnv] = useState<Env | null>(null);
  const [d, setD] = useState<Draft | null>(null);
  const [countries, setCountries] = useState<{ code: string; name: string }[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const body = (await call('/settings/signup')) as { settings: Policy; environment: Env };
      setSaved(body.settings);
      setEnv(body.environment);
      setD(toDraft(body.settings));
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [call]);

  useEffect(() => {
    void load();
    call('/admins').then((b) => setOperators(((b as { admins: Operator[] }).admins ?? [])
      .filter((a) => a.is_active))).catch(() => undefined);
    fetch(`${API_BASE}/signup/config`).then((r) => r.json())
      .then((c: { countries?: { code: string; name: string }[] }) => setCountries(c.countries ?? []))
      .catch(() => undefined);
  }, [load, call]);

  const changes = useMemo(() => {
    if (!d || !saved) return {};
    const out: Record<string, unknown> = {};
    const base = toDraft(saved);
    (Object.keys(d) as (keyof Draft)[]).forEach((k) => {
      if (k === 'captcha_secret_set') return;
      if (k === 'captcha_secret') {
        if (d.captcha_secret.trim()) out.captcha_secret = d.captcha_secret.trim();
        return;
      }
      if (k === 'captcha_max_score') {
        if (d.captcha_max_score.trim() !== base.captcha_max_score) out[k] = d.captcha_max_score.trim() || null;
        return;
      }
      if (d[k] !== base[k]) {
        out[k] = LIST_KEYS.includes(k) ? list(d[k] as string) : d[k];
      }
    });
    return out;
  }, [d, saved]);

  const save = async (extra?: Record<string, unknown>) => {
    setBusy(true);
    setErr(null);
    setOk(null);
    try {
      const body = (await call('/settings/signup', {
        method: 'PUT', body: JSON.stringify({ ...changes, ...(extra ?? {}) }),
      })) as { settings: Policy; environment: Env };
      setSaved(body.settings);
      setEnv(body.environment);
      setD(toDraft(body.settings));
      setOk('Saved. The signup page picks this up within a few seconds.');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (err && !d) return <Alert severity="error">{err}</Alert>;
  if (!d || !env) return <CircularProgress size={24} />;

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD({ ...d, [k]: v });
  const dirty = Object.keys(changes).length > 0;
  // Approvers: ticking an operator adds their address to the list; the box
  // below holds the whole list, so anyone outside the console can be added.
  const approvers = list(d.approval_emails).map((e) => e.toLowerCase());
  const toggleApprover = (email: string, on: boolean) => {
    const rest = list(d.approval_emails).filter((e) => e.toLowerCase() !== email.toLowerCase());
    set('approval_emails', (on ? [...rest, email] : rest).join(', '));
  };

  return (
    <Stack spacing={2}>
      {!env.multitenancy_enabled && (
        <Alert severity="error">
          Multi-tenancy is off on this server, so signup cannot create workspaces here.
        </Alert>
      )}
      {d.enabled && d.mode === 'AUTO' && !d.email_verification && (
        <Alert severity="error">
          Automatic creation without email verification lets anyone create a workspace under
          any email address. Turn verification on, or switch to operator approval.
        </Alert>
      )}
      {d.enabled && d.captcha_provider === 'none' && (
        <Alert severity="info">
          Recommended for a public signup page: turn on the CAPTCHA below. Rate limits and a
          honeypot field are always on, but they do not stop a determined bot.
        </Alert>
      )}
      {env.password_signup_blocker && d.allow_password && (
        <Alert severity="warning">Email + password signup is unavailable: {env.password_signup_blocker}</Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <FormControlLabel
          control={<Switch checked={d.enabled} onChange={(e) => set('enabled', e.target.checked)} />}
          label={<Typography variant="subtitle1">Allow new workspaces to sign up</Typography>}
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
          Shows “Create a workspace” on the sign-in page and opens /app/signup.
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>When someone signs up</Typography>
        <RadioGroup value={d.mode} onChange={(e) => set('mode', e.target.value as Policy['mode'])}>
          <FormControlLabel value="APPROVAL" control={<Radio size="small" />}
            label="An operator approves each new workspace before it is created (recommended)" />
          <FormControlLabel value="AUTO" control={<Radio size="small" />}
            label="Create the workspace automatically once the email is confirmed" />
        </RadioGroup>
        {d.mode === 'APPROVAL' && (
          <Box sx={{ ml: 4, mt: 1.5 }}>
            <Typography variant="subtitle2">Email approval requests to</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Sent when a new workspace is waiting for approval, with the company, workspace and
              requester. The console inbox always gets it too.
            </Typography>
            {operators.length > 0 && (
              <Stack sx={{ mb: 1 }}>
                {operators.map((o) => (
                  <FormControlLabel key={o.email}
                    control={<Checkbox size="small" checked={approvers.includes(o.email.toLowerCase())}
                      onChange={(e) => toggleApprover(o.email, e.target.checked)} />}
                    label={<Typography variant="body2">{o.full_name ? `${o.full_name} — ${o.email}` : o.email}</Typography>} />
                ))}
              </Stack>
            )}
            <TextField size="small" fullWidth label="Approval email recipients" value={d.approval_emails}
              onChange={(e) => set('approval_emails', e.target.value)}
              placeholder="approver@example.com, finance@example.com"
              helperText={`Comma-separated; tick operators above or type any address. Nobody chosen: the operators under “Pages & people” get it.${env.smtp_enabled ? '' : ' SMTP is OFF on this server, so no email is sent.'}`} />
          </Box>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>How the first administrator proves who they are</Typography>
        <Stack spacing={1}>
          <FormControlLabel
            control={<Switch checked={d.allow_password} onChange={(e) => set('allow_password', e.target.checked)} />}
            label="Email address + username + password" />
          <FormControlLabel sx={{ ml: 4 }}
            control={<Switch size="small" checked={d.email_verification}
              onChange={(e) => set('email_verification', e.target.checked)} />}
            label="Require the email to be confirmed with a link (recommended)" />
          <FormControlLabel
            control={<Switch checked={d.allow_google} onChange={(e) => set('allow_google', e.target.checked)} />}
            label="Google account" />
          <TextField size="small" label="Google OAuth client ID (Web)" value={d.google_client_id}
            onChange={(e) => set('google_client_id', e.target.value)}
            helperText="Also used for “Sign in with Google” on workspaces created by signup. Add this site's address to the client's Authorised JavaScript origins." />
          <TextField size="small" label="Public address of payroll" value={d.public_base_url}
            onChange={(e) => set('public_base_url', e.target.value)}
            placeholder="https://hrms.example.com/ukv-payroll"
            helperText={`Base of the links in emails. ${env.app_base_url ? `Falls back to APP_BASE_URL (${env.app_base_url}).` : 'Required for email verification in production.'} Mail delivery: ${env.smtp_enabled ? 'SMTP on' : 'SMTP OFF: no email is sent'}.`} />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Who may sign up</Typography>
        <Stack spacing={1.5}>
          <TextField size="small" label="Only these email domains (blank = any)" value={d.allowed_email_domains}
            onChange={(e) => set('allowed_email_domains', e.target.value)} placeholder="acme.com, globex.in" />
          <TextField size="small" label="Also refuse these email domains" value={d.blocked_email_domains}
            onChange={(e) => set('blocked_email_domains', e.target.value)}
            helperText="Known throwaway-inbox domains are always refused." />
          <FormControlLabel
            control={<Switch checked={d.block_free_email_providers}
              onChange={(e) => set('block_free_email_providers', e.target.checked)} />}
            label="Business addresses only (refuse gmail.com, yahoo.com, outlook.com…)" />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField size="small" type="number" label="Signups per network per day"
              value={d.max_signups_per_ip_per_day}
              onChange={(e) => set('max_signups_per_ip_per_day', Number(e.target.value))} />
            <TextField size="small" type="number" label="Workspaces per email"
              value={d.max_workspaces_per_email}
              onChange={(e) => set('max_workspaces_per_email', Number(e.target.value))} />
            <TextField size="small" type="number" label="Email link valid (hours)"
              value={d.verify_link_ttl_hours}
              onChange={(e) => set('verify_link_ttl_hours', Number(e.target.value))} />
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Bot protection (CAPTCHA)</Typography>
        <Stack spacing={1.5}>
          <TextField select size="small" label="CAPTCHA" value={d.captcha_provider}
            onChange={(e) => set('captcha_provider', e.target.value as Policy['captcha_provider'])}>
            {(Object.keys(CAPTCHA_LABEL) as Policy['captcha_provider'][]).map((p) => (
              <MenuItem key={p} value={p}>{CAPTCHA_LABEL[p]}</MenuItem>
            ))}
          </TextField>
          {d.captcha_provider !== 'none' && (
            <>
              {saved && saved.captcha_provider !== 'none' && saved.captcha_provider !== d.captcha_provider
                && !d.captcha_secret.trim() && (
                <Alert severity="warning">
                  Keys belong to one provider. Paste the {CAPTCHA_LABEL[d.captcha_provider]} site key
                  and secret key before saving, or signup will refuse every attempt.
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary">
                Keys from: {CAPTCHA_KEYS_FROM[d.captcha_provider]}. Add this server's domain to the
                key's allowed domains.
              </Typography>
              <TextField size="small" label="Site key" value={d.captcha_site_key}
                onChange={(e) => set('captcha_site_key', e.target.value)} />
              <TextField select size="small" label="Widget size" value={d.captcha_size}
                onChange={(e) => set('captcha_size', e.target.value as Policy['captcha_size'])}>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="compact">Compact</MenuItem>
              </TextField>
              {d.captcha_provider === 'hcaptcha' && (
                <TextField size="small" label="Highest risk score allowed (Enterprise)"
                  value={d.captcha_max_score} placeholder="Off"
                  onChange={(e) => set('captcha_max_score', e.target.value)}
                  helperText="hCaptcha Enterprise only: refuse a solve scored above this (0 = human, 1 = bot), e.g. 0.7. Blank = off. The free plan sends no score, so this has no effect there." />
              )}
              <TextField size="small" type="password" label="Secret key" value={d.captcha_secret}
                onChange={(e) => set('captcha_secret', e.target.value)}
                helperText={saved?.captcha_secret_set
                  ? 'A secret is stored (encrypted). Leave blank to keep it.'
                  : 'Stored encrypted; never shown again.'} />
              {saved?.captcha_secret_set && (
                <Box>
                  <Button size="small" color="error" onClick={() => void save({ captcha_secret: '-' })}>
                    Remove stored secret
                  </Button>
                </Box>
              )}
            </>
          )}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Pages & people</Typography>
        <Stack spacing={1.5}>
          <TextField size="small" label="Terms of service URL" value={d.terms_url}
            onChange={(e) => set('terms_url', e.target.value)} />
          <TextField size="small" label="Privacy policy URL" value={d.privacy_url}
            onChange={(e) => set('privacy_url', e.target.value)} />
          <TextField select size="small" label="Default country" value={d.default_country}
            onChange={(e) => set('default_country', e.target.value)}>
            {(countries.length ? countries : [{ code: d.default_country, name: d.default_country }]).map((c) => (
              <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>
            ))}
          </TextField>
          <TextField size="small" label="Email these operators about new workspaces" value={d.notify_emails}
            onChange={(e) => set('notify_emails', e.target.value)} placeholder="ops@example.com"
            helperText="Optional. Told when a signup creates a workspace, and about approval requests when nobody is chosen for those. The console inbox always gets them." />
        </Stack>
      </Paper>

      {err && <Alert severity="error" onClose={() => setErr(null)}>{err}</Alert>}
      {ok && <Alert severity="success" onClose={() => setOk(null)}>{ok}</Alert>}
      <Box sx={{ position: 'sticky', bottom: 0, py: 1, bgcolor: 'grey.100' }}>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" disabled={!dirty || busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save changes'}
          </Button>
          <Button disabled={!dirty || busy} onClick={() => saved && setD(toDraft(saved))}>Discard</Button>
        </Stack>
      </Box>
    </Stack>
  );
}
